import { getEditableTeams, getTeams } from "admin/rest_api";
import { Select } from "antd";
import { useEffectOnlyOnce } from "libs/react_hooks";
import Toast from "libs/toast";
import compact from "lodash-es/compact";
import unionBy from "lodash-es/unionBy";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import type { APITeam } from "types/api_types";

const { Option } = Select;

type TeamSelectionComponentProps = {
  value?: APITeam | Array<APITeam>;
  onChange?: (value: APITeam | Array<APITeam>) => void;
  afterFetchedTeams?: (arg0: Array<APITeam>) => void;
  mode?: "tags" | "multiple" | undefined;
  allowNonEditableTeams?: boolean;
  disabled?: boolean;
  prefix?: ReactNode;
};

function TeamSelectionComponent({
  value,
  onChange,
  afterFetchedTeams,
  mode,
  allowNonEditableTeams,
  disabled,
  prefix,
}: TeamSelectionComponentProps) {
  const [possibleTeams, setPossibleTeams] = useState<APITeam[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<APITeam[]>(value ? [value].flat() : []);
  const [isFetchingData, setIsFetchingData] = useState(false);

  // Sync selectedTeams with value
  useEffect(() => {
    setSelectedTeams(value ? [value].flat() : []);
  }, [value]);

  // Fetch teams on mount
  useEffectOnlyOnce(() => {
    fetchData();
  });

  async function fetchData() {
    setIsFetchingData(true);
    try {
      const possibleTeams = allowNonEditableTeams ? await getTeams() : await getEditableTeams();
      setPossibleTeams(possibleTeams);
      if (afterFetchedTeams) {
        afterFetchedTeams(possibleTeams);
      }
    } catch (_exception) {
      Toast.error("Could not load teams.");
    } finally {
      setIsFetchingData(false);
    }
  }

  const getAllTeams = useCallback((): APITeam[] => {
    return unionBy(possibleTeams, selectedTeams, (t) => t.id);
  }, [possibleTeams, selectedTeams]);

  const onSelectTeams = (selectedTeamIdsOrId: string | Array<string>) => {
    const selectedTeamIds = Array.isArray(selectedTeamIdsOrId)
      ? selectedTeamIdsOrId
      : [selectedTeamIdsOrId];
    const allTeams = getAllTeams();
    const selectedTeams = compact(selectedTeamIds.map((id) => allTeams.find((t) => t.id === id)));
    if (onChange) {
      onChange(Array.isArray(selectedTeamIdsOrId) ? selectedTeams : selectedTeams[0]);
    }
    setSelectedTeams(selectedTeams);
  };

  return (
    <Select
      mode={mode}
      style={{ width: "100%" }}
      placeholder={mode && mode === "multiple" ? "Select Teams" : "Select a Team"}
      showSearch={{ optionFilterProp: "children", filterOption: true }}
      onChange={onSelectTeams}
      value={selectedTeams.map((t) => t.id)}
      disabled={disabled ?? false}
      loading={isFetchingData}
      prefix={prefix}
    >
      {getAllTeams().map((team) => (
        <Option disabled={possibleTeams.find((t) => t.id === team.id) == null} key={team.id}>
          {team.name}
        </Option>
      ))}
    </Select>
  );
}

export default TeamSelectionComponent;
